import { Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetProductFilterOptions } from '~/api/product';
import { Input, Select } from '~/components/ui';
import { useDebounce } from '~/hooks/useDebounce';

export interface ProductFilters {
  species: string;
  dimension: string;
  grade: string;
  stockLevel: string;
  location: string;
  searchTerm: string;
}

interface ProductFilterProps {
  onChange: (filters: ProductFilters) => void;
}

export function ProductFilter({ onChange }: ProductFilterProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation('product');

  const { data: productFilters, isLoading } = useGetProductFilterOptions();

  const speciesOptions =
    productFilters?.lumberSpecies.map((s) => ({
      label: s.name,
      value: s.code,
    })) || [];
  speciesOptions.unshift({ label: t('product.filter.all_species'), value: '' });

  const gradeOptions =
    productFilters?.lumberGrades.map((g) => ({
      label: g.name,
      value: g.code,
    })) || [];
  gradeOptions.unshift({ label: t('product.filter.all_grades'), value: '' });

  const locationOptions =
    productFilters?.allUniqueProductInventoryLocationsForBuyer.map((l) => ({
      label: `${l.name} - ${l?.addressLine1} ${l?.city}, ${l?.region} ${l?.zipCode}`,
      value: l.id,
    })) || [];
  locationOptions.unshift({
    label: t('product.filter.all_locations'),
    value: '',
  });

  const dimensionsOptions =
    productFilters?.platformProductDimensions.map((d) => ({
      label: `${d.nominalThicknessInches}X${d.nominalWidthInches}`,
      value: `${d.nominalThicknessInches}X${d.nominalWidthInches}`,
    })) || [];
  dimensionsOptions.unshift({
    label: t('product.filter.all_dimensions'),
    value: '',
  });

  const [filters, setFilters] = useState<ProductFilters>({
    species: '',
    dimension: '',
    grade: '',
    stockLevel: '',
    location: '',
    searchTerm: '',
  });

  function handleChange<K extends keyof ProductFilters>(
    key: K,
    value: ProductFilters[K]
  ) {
    let updated = { ...filters, [key]: value };
    setFilters((prev) => {
      updated = { ...prev, [key]: value };
      return updated;
    });
    onChange(updated);
  }

  const debouncedSearchChange = useDebounce(handleChange, 1000);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearchChange('searchTerm', value);
  };

  const stockLevelOptions = [
    { label: t('product.filter.all_stock_levels'), value: '' },
    { label: t('product.filter.stock_low'), value: 'low' },
    { label: t('product.filter.stock_medium'), value: 'medium' },
    { label: t('product.filter.stock_high'), value: 'high' },
  ];
  if (isLoading) {
    return <p className="w-full text-center">{t('product.filter.loading')}</p>;
  }
  return (
    <div className="bg-neut-bg-alt rounded-lg border border-neut-border p-4 sm:p-6 shadow-sm">
      <div className="grid grid-cols-1 gap-4">
        <div className="relative">
          <Input
            name="searchTerm"
            type="text"
            placeholder={t('product.filter.search_placeholder')}
            value={searchTerm}
            onChange={(_, value) => handleSearchChange(value)}
            leftIcon={
              <Search size={18} className="text-neut-400" aria-hidden="true" />
            }
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            name={'species'}
            value={filters.species}
            options={speciesOptions || []}
            onChange={(_, value) => handleChange('species', value)}
          />

          <Select
            name={'dimension'}
            value={filters.dimension}
            options={dimensionsOptions || []}
            onChange={(_, value) => handleChange('dimension', value)}
          />

          <Select
            name={'grade'}
            value={filters.grade}
            options={gradeOptions}
            onChange={(_, value) => handleChange('grade', value)}
          />

          <Select
            name={'stockLevel'}
            value={filters.stockLevel}
            options={stockLevelOptions}
            onChange={(_, value) => handleChange('stockLevel', value)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            name={'location'}
            value={filters.location}
            options={locationOptions || []}
            onChange={(_, value) => handleChange('location', value)}
          />
        </div>
      </div>
    </div>
  );
}
